/**
 * EnvironmentComponent — R3F component that manages environment rendering.
 *
 * Responsibilities:
 *  1. Creates a PMREMGenerator and compiles the equirectangular shader once.
 *  2. On mount: loads the initial HDR from IBLManager state (no fade).
 *  3. When the EnvironmentManager or IBLManager signals a change, runs the
 *     transition machine: fade-out (0→1) → load new HDR → apply background → fade-in (1→0)
 *  4. Live-updates scene.environmentIntensity, environmentRotation, backgroundIntensity every frame.
 *  5. Background is set INDEPENDENTLY of IBL based on BackgroundDescriptor.
 *  6. Conditionally renders DomeBackground, GroundPlane, and scene fog.
 *
 * Decoupled: Does NOT touch sunlight or shadow. Those are now standalone
 * subsystems. The PresetManager orchestrates all systems from named presets.
 *
 * @see docs/ims-engine-api/10-feature-environment.md
 */

import { useState, useEffect, useRef, Suspense, useSyncExternalStore } from 'react';
import { useThree, useFrame, useLoader } from '@react-three/fiber';
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
// DreiEnvironment removed — scene.background set imperatively to avoid drei's
// useLayoutEffect resetting scene.environmentRotation/backgroundRotation on re-render.
import type { EnvironmentManager } from './EnvironmentManager';
import type { EnvironmentState } from './EnvironmentManager';
import type { IBLManager, IBLState } from './IBLManager';
import type { ViewerSettingsManager } from '../core/ViewerSettingsManager';
import type { AssetLoader } from '../core/AssetLoader';
import { applyBackground, applyFog, resolveHdrUrl } from './environmentUtils';
import { DomeBackground } from './DomeBackground';
import { GroundPlane } from './GroundPlane';

// ---------------------------------------------------------------------------
// Shadow opacity compensation for PostFX
// ---------------------------------------------------------------------------
// When the EffectComposer is active, alpha blending happens in LINEAR space
// before tone-mapping.  ACES Filmic's toe curve lifts dark regions, making
// shadows appear lighter.  This multiplier compensates.
// ---------------------------------------------------------------------------
const POSTFX_SHADOW_OPACITY_BOOST = 1.4;
const SUN_SHADOW_BASE_OPACITY = 0.35;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface EnvironmentComponentProps {
  manager: EnvironmentManager;
  /** Standalone IBL manager — controls HDR file + intensity. */
  iblManager: IBLManager;
  /**
   * Base path where HDR files are served.
   * Must match how the brand app exposes `engine/public/assets/hdri/`.
   * Default: '/assets/hdri'
   */
  hdriBasePath?: string;
  /** AssetLoader instance — routes HDR loading through cache + retry + progress. */
  assetLoader: AssetLoader;
  /** Optional — when provided, sun shadow opacity is boosted while PostFX is active. */
  viewerSettingsManager?: ViewerSettingsManager;
  /**
   * Optional ref to the root THREE.Group wrapping all environment geometry.
   * Allows the pano callbacks to imperatively hide/show every environment
   * object (ground plane, dome, road markings, spotlight panels, etc.) in
   * one shot — the same pattern as modelSceneRef.
   */
  groupRef?: { current: THREE.Group | null };
}

// ---------------------------------------------------------------------------
// Transition phase machine
// ---------------------------------------------------------------------------

type Phase = 'idle' | 'fading-out' | 'loading' | 'fading-in';

const FADE_DURATION = 0.3; // seconds

// ---------------------------------------------------------------------------
// EnvironmentComponent
// ---------------------------------------------------------------------------

export function EnvironmentComponent({
  manager,
  iblManager,
  hdriBasePath = '/assets/hdri',
  assetLoader,
  viewerSettingsManager,
  groupRef,
}: EnvironmentComponentProps) {
  const { gl, scene } = useThree();

  // ── PostFX shadow compensation ──────────────────────────────────────────────
  // Subscribe to ViewerSettingsManager to re-render when PostFX toggles change.
  useSyncExternalStore(
    (cb) => viewerSettingsManager?.subscribe(cb) ?? (() => {}),
    () => viewerSettingsManager?.version ?? 0,
  );
  const postFxActive = viewerSettingsManager
    ? (viewerSettingsManager.getState().bloom.enabled || viewerSettingsManager.getState().antialiasing === 'smaa')
    : false;
  const sunShadowOpacity = Math.min(
    SUN_SHADOW_BASE_OPACITY * (postFxActive ? POSTFX_SHADOW_OPACITY_BOOST : 1),
    1,
  );

  // ── State split ─────────────────────────────────────────────────────────────
  // pendingStateRef: what EnvironmentManager currently wants (set by onChange)
  // pendingIBLRef:   what IBLManager currently wants (set by onChange)
  // liveStateRef:    what is ACTUALLY rendered right now (swapped behind curtain)
  // liveIBLRef:      what IBL is ACTUALLY loaded right now
  // liveReactState:  React-state mirror of liveStateRef for DomeBackground/GroundPlane
  const pendingStateRef = useRef<EnvironmentState>(manager.getState());
  const pendingIBLRef   = useRef<IBLState>(iblManager.getState());
  const liveStateRef    = useRef<EnvironmentState>(manager.getState());
  const liveIBLRef      = useRef<IBLState>(iblManager.getState());
  const [liveReactState, setLiveReactState] = useState<EnvironmentState>(manager.getState());

  // No React state for background — scene.background / scene.backgroundBlurriness /
  // scene.backgroundIntensity are all written imperatively every useFrame + applyHdr.
  // This avoids drei's useLayoutEffect resetting scene.environmentRotation on re-render.

  // All real data lives in refs to keep useFrame closures fresh
  const pmremRef   = useRef<THREE.PMREMGenerator | null>(null);
  const phaseRef   = useRef<Phase>('idle');
  const fadeClock  = useRef(0);
  /** `${id}|${hdrFile}` of what is currently loaded in the scene. */
  const loadedKeyRef   = useRef<string | null>(null);
  const loadedHdrRef   = useRef<string | null>(null);
  const envMapRef      = useRef<THREE.Texture | null>(null);
  /**
   * Raw equirectangular HDR — used for scene.background.
   * Three.js internally converts equirect → PMREM with proper face-seam
   * padding when backgroundBlurriness > 0.  Using the raw texture (like
   * drei's <Environment blur>) avoids the visible cubemap grid that appears
   * when a pre-baked PMREM atlas is blurred.
   */
  const rawHdrRef      = useRef<THREE.Texture | null>(null);
  const isLoadingRef   = useRef(false);
  const overlayRef     = useRef<HTMLDivElement | null>(null);
  const sceneRef       = useRef(scene);
  const basePathRef    = useRef(hdriBasePath);
  /** Ref to R3F invalidate — lets async triggerLoad request a frame after completion. */
  const invalidateRef  = useRef<(() => void) | null>(null);

  // Keep refs current every render
  sceneRef.current      = scene;
  basePathRef.current   = hdriBasePath;

  // ---------------------------------------------------------------------------
  // Apply loaded HDR to scene
  // ---------------------------------------------------------------------------

  /** Load HDR, generate PMREM env-map, apply to scene. Ref avoids stale closures. */
  const assetLoaderRef = useRef(assetLoader);
  assetLoaderRef.current = assetLoader;
  const applyHdr = useRef(async (ibl: IBLState, env: EnvironmentState) => {
    if (!pmremRef.current) return;
    // hdrUrl takes precedence over hdrFile when set (custom / user-uploaded HDR)
    const hdrKey = ibl.hdrUrl || ibl.hdrFile;
    const url = ibl.hdrUrl || resolveHdrUrl(ibl.hdrFile, basePathRef.current);

    // Skip re-loading if the same HDR is already loaded
    const needsLoad = loadedHdrRef.current !== hdrKey;
    if (needsLoad) {
      const rawTexture = await assetLoaderRef.current.loadHDR(url) as THREE.DataTexture;
      const envMap = pmremRef.current.fromEquirectangular(rawTexture).texture;

      // Keep raw equirect alive for background rendering (drei-style blur).
      // Three.js converts equirect → internal PMREM with proper face padding
      // when backgroundBlurriness > 0, avoiding cubemap grid artifacts.
      rawTexture.mapping = THREE.EquirectangularReflectionMapping;
      if (rawHdrRef.current) rawHdrRef.current.dispose();
      rawHdrRef.current = rawTexture;

      const sc = sceneRef.current;
      // Dispose the old envMap if it exists and differs
      if (sc.environment && sc.environment !== envMap) {
        sc.environment.dispose();
      }

      sc.environment           = envMap;
      sc.environmentIntensity  = ibl.intensity;
      sc.environmentRotation.y = ibl.envRotationY;
      sc.backgroundRotation.y  = ibl.envRotationY;
      envMapRef.current        = envMap;
      loadedHdrRef.current     = hdrKey;
    } else {
      sceneRef.current.environmentIntensity  = ibl.intensity;
      sceneRef.current.environmentRotation.y = ibl.envRotationY;
      sceneRef.current.backgroundRotation.y  = ibl.envRotationY;
    }

    // Apply background directly — no React state, no drei re-render side-effects.
    // Three.js converts equirect → PMREM internally when backgroundBlurriness > 0.
    const sc = sceneRef.current;
    sc.backgroundIntensity   = ibl.backgroundIntensity;
    sc.environmentRotation.y = ibl.envRotationY;
    sc.backgroundRotation.y  = ibl.envRotationY;
    if (ibl.backgroundVisible || env.background.type === 'hdr-blur') {
      sc.background            = rawHdrRef.current;
      sc.backgroundBlurriness  = ibl.backgroundBlur;
    } else {
      // Non-blur backgrounds (color, dome, none) — set directly
      applyBackground(sc, env.background, rawHdrRef.current);
    }
  });

  // Keep applyHdr ref always reflecting latest scene/basePath (done via sceneRef/basePathRef above)

  // ---------------------------------------------------------------------------
  // Trigger a load (used from phase machine — safe to call from useFrame)
  // ---------------------------------------------------------------------------

  const triggerLoad = useRef((env: EnvironmentState, ibl: IBLState, onDone?: () => void) => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    applyHdr.current(ibl, env)
      .then(() => {
        loadedKeyRef.current  = `${env.id}|${ibl.hdrUrl || ibl.hdrFile}`;
        isLoadingRef.current  = false;
        // No sunlight/shadow coupling — each subsystem stands alone.
        // Use PresetManager to orchestrate all systems from a named preset.
        onDone?.();
        // frameloop="demand": guarantee a frame renders the newly loaded HDR
        invalidateRef.current?.();
      })
      .catch((err) => {
        // Non-fatal: leave previous HDR in place, reset loading flag
        console.warn('[EnvironmentComponent] HDR load failed:', err);
        isLoadingRef.current = false;
        phaseRef.current     = 'idle';
        invalidateRef.current?.();
      });
  });

  // ---------------------------------------------------------------------------
  // Subscribe to EnvironmentManager
  // ---------------------------------------------------------------------------

  // Needed here (before useFrame) so onChange handlers can bootstrap a frame
  const invalidate = useThree((s) => s.invalidate);
  invalidateRef.current = invalidate;

  useEffect(() => {
    // Proactively preload dome assets into R3F's Suspense cache for the initial state.
    // The engine's PreloadManager already downloaded them to the browser HTTP cache,
    // but R3F's useLoader has its own cache.  Calling useLoader.preload() here starts
    // parsing the assets in the background so that when DomeBackground is rendered
    // (after the env curtain fades in) it resolves instantly instead of suspending
    // for ~1 second and showing the null fallback.
    const preloadDomeAssets = (s: ReturnType<typeof manager.getState>) => {
      if (s.background.type === 'dome') {
        useLoader.preload(GLTFLoader, s.background.meshUrl);
        if (s.background.textureUrl) {
          useLoader.preload(THREE.TextureLoader, s.background.textureUrl);
        }
      }
    };

    // Preload for the current pending state (set before applyScene fires)
    preloadDomeAssets(pendingStateRef.current);

    // eslint-disable-next-line react-hooks/immutability -- intentional: wires EnvironmentManager.onChange to R3F render bridge
    manager.onChange = (s) => {
      pendingStateRef.current = s;
      // Preload dome assets as soon as we know the target env is a dome —
      // this fires at the START of the curtain fade-out, giving us the full
      // fade + HDR-load window before DomeBackground needs to render.
      preloadDomeAssets(s);
      // frameloop="demand": kick one frame so useFrame detects the pending change
      invalidate();
    };
    // eslint-disable-next-line react-hooks/immutability -- intentional: wires IBLManager.onChange to R3F render bridge
    iblManager.onChange = (ibl) => {
      pendingIBLRef.current = ibl;
      invalidate();
    };
    return () => {
      manager.onChange = undefined;
      iblManager.onChange = undefined;
    };
  }, [manager, iblManager, invalidate]);

  // Mount the black-fade overlay directly on document.body so it covers the full
  // viewport — Html fullscreen is constrained to canvas bounds and misses UI bars.
  useEffect(() => {
    const div = document.createElement('div');
    div.style.cssText =
      'position:fixed;inset:0;background:#000;opacity:0;pointer-events:none;z-index:9999;';
    document.body.appendChild(div);
    overlayRef.current = div;
    return () => {
      document.body.removeChild(div);
      overlayRef.current = null;
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Mount: create PMREMGenerator + initial HDR load (no transition)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    pmremRef.current = new THREE.PMREMGenerator(gl);
    pmremRef.current.compileEquirectangularShader();

    // Initial load — instant, no fade.
    // IMPORTANT: capture s/ibl NOW and use them (not pendingStateRef) in the
    // callback. applyScene() may fire during the async HDR load (via the
    // sceneLoaded event) and update pendingIBLRef before the callback runs.
    // If we set liveIBLRef = pendingIBLRef we end up with liveKey === pendingKey
    // even though the actual loaded texture is the old preset HDR → the phase
    // machine never detects a change and the scene stays on the wrong HDR.
    const s = pendingStateRef.current;
    const ibl = pendingIBLRef.current;
    triggerLoad.current(s, ibl, () => {
      loadedKeyRef.current = `${s.id}|${ibl.hdrUrl || ibl.hdrFile}`;
      // Set live to what was actually loaded — NOT what pending may have become.
      // If pending changed during load the phase machine will detect the diff
      // and trigger a curtain transition in the next useFrame tick.
      liveStateRef.current = s;
      liveIBLRef.current   = ibl;
      setLiveReactState(s);
    });

    return () => {
      pmremRef.current?.dispose();
      pmremRef.current = null;
      if (sceneRef.current.environment) {
        sceneRef.current.environment.dispose();
        sceneRef.current.environment = null;
      }
      rawHdrRef.current?.dispose();
      rawHdrRef.current = null;
      sceneRef.current.background = null;
    };
  }, [gl]); // gl is stable — runs exactly once

  // ---------------------------------------------------------------------------
  // useFrame — transition phase machine + live intensity/blur updates
  // ---------------------------------------------------------------------------

  useFrame((_, delta) => {
    const pending    = pendingStateRef.current;
    const pendingIBL = pendingIBLRef.current;
    const live       = liveStateRef.current;
    const liveIBL    = liveIBLRef.current;
    const overlay    = overlayRef.current;
    const sc         = sceneRef.current;
    const phase      = phaseRef.current;

    // HDRI disabled — clear everything and bail
    if (!pending.enabled) {
      if (sc.environment) { sc.environment.dispose(); sc.environment = null; }
      rawHdrRef.current?.dispose(); rawHdrRef.current = null;
      sc.background = null;
      sc.fog = null;
      loadedKeyRef.current = null;
      loadedHdrRef.current = null;
      phaseRef.current = 'idle';
      if (overlay) overlay.style.opacity = '0';
      return;
    }

    // Re-trigger load if HDRI was just re-enabled (no key loaded yet)
    if (loadedKeyRef.current === null && !isLoadingRef.current && pmremRef.current) {
      liveStateRef.current = pending;
      liveIBLRef.current   = pendingIBL;
      setLiveReactState(pending);
      triggerLoad.current(pending, pendingIBL, () => { phaseRef.current = 'idle'; });
      return;
    }

    // ── Idle: live-update IBL values — but FREEZE them at liveIBL when a
    //         scene-switch transition is pending so nothing snaps before the curtain ──
    if (phase === 'idle') {
      // Detect pending transition FIRST so we can gate which IBL values to apply.
      // A transition is "pending" when the loaded scene/HDR key differs from what
      // pending wants — meaning a curtain fade is about to start this same frame.
      const liveKey    = `${live.id}|${liveIBL.hdrUrl || liveIBL.hdrFile}`;
      const pendingKey = `${pending.id}|${pendingIBL.hdrUrl || pendingIBL.hdrFile}`;
      const transitionPending = loadedKeyRef.current !== null && pendingKey !== liveKey;

      // While a transition is pending: freeze ALL visible IBL values at liveIBL —
      // nothing should change until the curtain is fully black.
      // While idle with no transition: follow pendingIBL immediately so in-place
      // tweaks (setIBL calls that don't change the HDR) have instant feedback.
      const activeIBL = transitionPending ? liveIBL : pendingIBL;

      if (sc.environment) {
        sc.environmentIntensity  = activeIBL.intensity;
        sc.environmentRotation.y = activeIBL.envRotationY;
        sc.backgroundRotation.y  = activeIBL.envRotationY;
        sc.backgroundIntensity   = activeIBL.backgroundIntensity;

        // Set background directly — no React state / drei re-render.
        if (activeIBL.backgroundVisible || live.background.type === 'hdr-blur') {
          if (rawHdrRef.current) {
            sc.background           = rawHdrRef.current;
            sc.backgroundBlurriness = activeIBL.backgroundBlur;
          }
        } else {
          // Non-blur backgrounds (color, dome, none) — set directly
          applyBackground(sc, live.background, rawHdrRef.current);
        }
      }
      applyFog(sc, live.fogConfig); // fog always from LIVE (no pre-snap)

      if (transitionPending) {
        if (pending.transition) {
          phaseRef.current  = 'fading-out';
          fadeClock.current = 0;
          // frameloop="demand": bootstrap the curtain animation loop
          invalidate();
        } else {
          // Instant switch — commit live immediately then load
          liveStateRef.current = pending;
          liveIBLRef.current   = pendingIBL;
          setLiveReactState(pending);
          applyFog(sc, pending.fogConfig);
          phaseRef.current = 'loading';
          triggerLoad.current(pending, pendingIBL, () => { phaseRef.current = 'idle'; });
          invalidate();
        }
      } else if (pending !== live || pendingIBL !== liveIBL) {
        // Override-only change (domeTint, groundColor, intensity, rotation, etc.) —
        // no curtain needed. Commit pending → live so DomeBackground/GroundPlane re-render.
        liveStateRef.current = pending;
        liveIBLRef.current   = pendingIBL;
        setLiveReactState(pending);
      }
      return;
    }

    // ── Fading out (0 → 1): curtain closes ──────────────────────────────────
    if (phase === 'fading-out') {
      fadeClock.current += delta;
      const opacity = Math.min(fadeClock.current / FADE_DURATION, 1);
      if (overlay) overlay.style.opacity = String(opacity);
      invalidate(); // Keep rendering during curtain animation

      // Signal: curtain is fully black — NOW commit the new state and load
      if (opacity >= 1 && !isLoadingRef.current) {
        // Commit pending → live BEHIND the curtain
        liveStateRef.current = pending;
        liveIBLRef.current   = pendingIBL;
        setLiveReactState(pending); // swaps DomeBackground / GroundPlane
        applyFog(sc, pending.fogConfig);

        phaseRef.current = 'loading';
        triggerLoad.current(pending, pendingIBL, () => {
          phaseRef.current  = 'fading-in';
          fadeClock.current = 0;
        });
      }
      return;
    }

    // ── Loading: async in progress, curtain stays at 100% opacity ───────────
    if (phase === 'loading') {
      if (overlay) overlay.style.opacity = '1';
      invalidate(); // Keep polling until load completes
      return;
    }

    // ── Fading in (1 → 0): curtain opens ────────────────────────────────────
    if (phase === 'fading-in') {
      fadeClock.current += delta;
      const opacity = Math.max(1 - fadeClock.current / FADE_DURATION, 0);
      if (overlay) overlay.style.opacity = String(opacity);
      if (opacity <= 0) {
        phaseRef.current = 'idle';
      }
      // Keep rendering during curtain animation
      invalidate();
    }
  });

  // ---------------------------------------------------------------------------
  // Render: dome background + ground plane + shadow catcher (conditional on descriptor)
  // ---------------------------------------------------------------------------

  const s = liveReactState;
  const bg = s.background;
  const ground = s.descriptor?.ground;
  const domeTint = s.overrides?.domeTint;

  // Determine if drei's <Environment> should manage the background
  // (removed — scene.background is now set entirely imperatively)
  // const useDreiBg = bgTexture !== null;

  return (
    <group ref={groupRef}>
      {/* scene.background + scene.backgroundBlurriness are written imperatively
          in useFrame and applyHdr — no drei <Environment> needed. This avoids
          drei's useLayoutEffect resetting scene.environmentRotation/backgroundRotation. */}
      {bg.type === 'dome' && (
        <Suspense fallback={null}>
          <DomeBackground meshUrl={bg.meshUrl} textureUrl={bg.textureUrl} tint={domeTint} />
        </Suspense>
      )}
      {/* Depth-only clip-plane: invisible (colorWrite=false) but writes
          depth at the same Y as a regular GroundPlane. This ensures tyres
          sunk via groundSinkY are visually clipped even in environments
          without a visible ground (Studio dome, HDRI).  Environments that
          already render a GroundPlane (Spotlight, Road) don't need this —
          their depthWrite-enabled ground handles clipping automatically. */}
      {!ground?.visible && bg.type !== 'none' && (
        <mesh position={[0, -0.001, 0]} rotation={[-Math.PI / 2, 0, 0]} renderOrder={-500}>
          <circleGeometry args={[25, 64]} />
          <meshBasicMaterial colorWrite={false} depthWrite />
        </mesh>
      )}
      {ground?.visible && (
        <GroundPlane
          type={ground.type}
          color={s.groundColor ?? ground.color}
          textureUrl={
            s.groundVariant === 'road' && s.roadSurface === 'racetrack'
              ? '/assets/textures/racetrack-1k.png'
              : ground.textureUrl
          }
          normalUrl={ground.normalUrl}
          repeat={ground.repeat}
          size={ground.size}
          groundVariant={s.groundVariant}
          groundReflectivity={s.groundReflectivity}
          roadMirror={s.roadMirror}
        />
      )}
      {/* Shadow catcher — hidden when reflective ground is active because:
          1. ReflectorGroundPlane has receiveShadow built-in
          2. Separate shadow plane causes z-fighting artifacts with the reflector's
             internal render camera (sees black underside → flashing artefacts)
          For 'road' variant the shadow catcher sits ABOVE the road markings so
          contact shadows are visible on top of the transparent road plane. */}
      {s.enabled && s.descriptor && s.groundVariant !== 'reflective' && (
        <mesh
          position={[0, -0.0004, 0]}
          rotation={[-Math.PI / 2, 0, 0]}
          receiveShadow
          renderOrder={-490}
        >
          <circleGeometry args={[25, 64]} />
          <shadowMaterial opacity={sunShadowOpacity} transparent />
        </mesh>
      )}
    </group>
  );
}
